import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import Channels from 'components/Channels';
import TestCaseCard from 'components/TestCase/TestCaseCard';
import LoadingSpinner from 'components/LoadingSpinner';
import NotificationField from 'components/NotificationField';
import JiraFields from 'components/JiraFields';
import WeaveMuiThemeProvider from 'components/WeaveMuiThemeProvider';

import MetafieldsModal from 'components/MetafieldsModal';
import BulkUploadModal from 'components/BulkUploadModal';
import ConfirmationModal from 'components/ConfirmationModal';
import SubmitTestCaseModal from 'components/SubmitTestCaseModal';
import { transformTestData } from '../../utils/transformTestDataUtils';

import {
  submitTestCases,
  removeHostingProject,
  loadGitBranches,
} from 'store/actions/hosting_actions';
import { addNewNotification } from 'store/actions/notification_actions';
import filterList from 'selectors/search';
import {
  getHostingProjects,
  getHostingProjectTestCases,
  postHostingNewProject,
  postHostingNewTestCase,
  putHostingEditTestCase,
  deleteHostingTestCase,
  postHostingCreateTestDataFile,
  deleteHostingTestDataFile,
  postHostingCreateExecutionTestDataFile,
  postHostingCreateTestData,
  getGitBranches,
  postGitHubFetchTestCases,
} from 'utils/api.service';
import { setDefaultFilter } from 'store/actions/jobsQueue_actions';
import HostingControls from './HostingControls';
import MoveTestCasesModal from './MoveTestCasesModal';
import GitTestCaseModal from './GitTestCaseModal';
import SelectGitHubTestCasesModal from './SelectGitHubTestCasesModal';
import DeletedGitTestCasesModal from '../../components/DeletedGitTestCasesModal/DeletedGitTestCasesModal';
import { BUILD_TOOLS } from 'utils/consts';
import { formatSubmissionErrorDetail } from 'utils/formatSubmissionError';
import BranchModal from './BranchModal';
import EmptyTestCasesState from './EmptyTestCasesState';
import EmptyProjectsState from './EmptyProjectsState';
import HostingTable from './HostingTable';
import AddTestCaseModal from 'components/AddTestCaseModal';

class Hosting extends React.Component {
  state = {
    projects: [],
    activeProject: {},
    projectMetafields: [],
    testCases: [],
    testCaseMetafields: [],
    searchValue: '',
    showModal: false,
    modal: '',
    modalElement: null,
    deleteTestCaseInProgress: false,
    executionname: '',
    notificationEmail: '',
    selectAll: false,
    viewType: 'grid',
    isLoading: false,
    targetProject: '',
    gitBranch: '',
    fetchedTestCases: [],
    deletedGitTestCases: [],
    selectedTestCasesForImport: [],
    isSubmittingTestCases: false,
  };

  constructor() {
    super();

    this.onProjectSelected = this.onProjectSelected.bind(this);
    this.onTestCaseSelected = this.onTestCaseSelected.bind(this);
    this.onSearchInputChange = this.onSearchInputChange.bind(this);
    this.onCommonFieldChange = this.onCommonFieldChange.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
    this.closeModal = this.closeModal.bind(this);

    this.submitNewProject = this.submitNewProject.bind(this);
    this.submitUploadTestCases = this.submitUploadTestCases.bind(this);
    this.submitNewTestCase = this.submitNewTestCase.bind(this);
    this.submitEditTestCase = this.submitEditTestCase.bind(this);
    this.submitAddTestData = this.submitAddTestData.bind(this);
    this.submitTestCases = this.submitTestCases.bind(this);
    this.submitGitHubTestCases = this.submitGitHubTestCases.bind(this);
    this.createGitHubTestCases = this.createGitHubTestCases.bind(this);
    this.handleDeletedTestCasesContinue =
      this.handleDeletedTestCasesContinue.bind(this);
    this.onNotificationEmailChange = this.onNotificationEmailChange.bind(this);
    this.onSelectAllClicked = this.onSelectAllClicked.bind(this);
    this.onViewChangeClicked = this.onViewChangeClicked.bind(this);
    this.setTargetProject = this.setTargetProject.bind(this);
    this.moveTestCase = this.moveTestCase.bind(this);
    this.moveTestCasesBulk = this.moveTestCasesBulk.bind(this);
    this.editTestCasesBulk = this.editTestCasesBulk.bind(this);
    this.deleteTestCasesBulk = this.deleteTestCasesBulk.bind(this);
    this.onBranchChange = this.onBranchChange.bind(this);
    this.navigateToAddProject = this.navigateToAddProject.bind(this);
    this.navigateToManageTestData = this.navigateToManageTestData.bind(this);
    this.navigateToAddTestCase = this.navigateToAddTestCase.bind(this);
    this.navigateToEditTestCase = this.navigateToEditTestCase.bind(this);
  }

  componentDidMount() {
    Promise.all([
      this.loadProjects(),
      this.fetchBranches(this.props.org, this.props.workstream),
    ]);
  }

  componentDidUpdate(props) {
    // When coming back from nested Hosting pages (Add/Edit Project, etc.),
    // avoid refetching by default (component is kept mounted), but allow
    // an explicit refresh when a nested page indicates data changed.
    const refreshHosting = this.props.location?.state?.refreshHosting;
    const refreshProject = this.props.location?.state?.refreshProject;
    const wasExact = props.match?.isExact;
    const isExact = this.props.match?.isExact;

    if (!wasExact && isExact && refreshProject) {
      const project = this.state.projects.find(
        (p) => p.projectname === refreshProject
      );
      if (project) {
        this.loadProject(this.props.org, this.props.workstream, refreshProject);
      }
      this.props.history.replace(this.props.location.pathname);
    } else if (!wasExact && isExact && refreshHosting) {
      Promise.all([
        this.loadProjects(),
        this.fetchBranches(this.props.org, this.props.workstream),
      ]).finally(() => {
        // Clear the flag so refresh doesn't repeat on future navigations.
        this.props.history.replace(this.props.location.pathname);
      });
    }

    if (props.workstreamName !== this.props.workstreamName) {
      Promise.all([
        this.loadProjects(),
        this.fetchBranches(this.props.org, this.props.workstream),
      ]);
    }
  }

  fetchBranches(org, workstream) {
    return getGitBranches(org, workstream).then((response) => {
      this.props.loadGitBranches(response.data);
    });
  }

  // data loading
  loadProjects() {
    const { props } = this;

    this.setState(
      {
        projects: [],
        projectMetafields: [],
        activeProject: {},
        testCases: [],
        testCaseMetafields: [],
        selectAll: false,
      },
      () => {
        getHostingProjects(props.org, props.workstream).then((response) => {
          let activeProject = {};

          if (response.data.project.length) {
            activeProject = response.data.project[0];
          }

          this.setState({
            projects: response.data.project,
            projectMetafields: response.data.metafield,
            activeProject,
            targetProject: activeProject.projectname,
          });

          if (activeProject.projectname) {
            this.setState({
              projectMetafields: response.data.metafield,
              activeProject,
              notificationEmail: props.userInfo.emailid,
              gitBranch: activeProject.gitbranchname,
            });

            this.loadTestCases(
              props.org,
              props.workstream,
              activeProject.projectname
            );
          }
        });
      }
    );
  }

  loadProject(org, workstream, projectname) {
    const activeProject = this.state.projects.find(
      (p) => p.projectname === projectname
    );

    this.setState(
      {
        activeProject,
        targetProject: activeProject.projectname,
        gitBranch: activeProject.gitbranchname,
        testCases: [],
        testCaseMetafields: [],
        selectAll: false,
      },
      () => {
        this.loadTestCases(org, workstream, projectname);
      }
    );
  }

  loadTestCases(org, workstream, project) {
    this.setState(
      {
        testCases: [],
        testCaseMetafields: [],
        loadingTestCases: true,
      },
      () => {
        getHostingProjectTestCases(org, workstream, project).then(
          (response) => {
            let testCases = response.data.results.map((t) => {
              t.testdata = t.testdata || {};

              let envObjExist = this.state.activeProject.envlist.some(
                (env) =>
                  t.testdata.hasOwnProperty(env.displayname.toUpperCase()) &&
                  typeof t.testdata[env.displayname.toUpperCase()] === 'object'
              );

              if (Object.keys(t.testdata).length > 0 && !envObjExist) {
                let oldTestData = t.testdata;
                t.testdata = {};
                this.state.activeProject.envlist.forEach(
                  (e) => (t.testdata[e.displayname.toUpperCase()] = oldTestData)
                );
              } else if (Object.keys(t.testdata).length === 0) {
                this.state.activeProject.envlist.forEach(
                  (e) => (t.testdata[e.displayname.toUpperCase()] = {})
                );
              }

              return { ...t, checked: false };
            });
            this.setState({
              testCases: testCases,
              testCaseMetafields: response.data.metafield,
              loadingTestCases: false,
            });
          }
        );
      }
    );
  }

  // user actions
  onProjectSelected(projectname) {
    const activeProject = this.state.projects.find(
      (p) => p.projectname === projectname
    );

    this.setState({
      activeProject,
      testCases: [],
      testCaseMetafields: [],
      selectAll: false,
    });

    this.loadProject(this.props.org, this.props.workstream, projectname);
  }

  onTestCaseSelected(testCaseID) {
    const { testCases, selectAll } = this.state;
    const testCaseIndex = testCases.findIndex((t) => t.id === testCaseID);

    testCases[testCaseIndex].checked = !testCases[testCaseIndex].checked;

    if (selectAll && !testCases[testCaseIndex].checked) {
      this.setState({ selectAll: false });
    }

    this.setState({ testCases });
  }

  onSearchInputChange(e) {
    this.setState({
      searchValue: e.target.value,
    });
  }

  onCommonFieldChange({ target }) {
    this.setState({ [target.name]: target.value });
  }

  onNotificationEmailChange(value) {
    this.setState({ notificationEmail: value });
  }

  onSelectAllClicked(e) {
    const { checked } = e.target;
    const { testCases } = this.state;
    const filteredTests = filterList(
      testCases,
      this.state.searchValue,
      'displayname'
    )
      .filter((test) => {
        return test.status[this.props.activeEnvironment] !== 'disabled';
      })
      .map((test) => test.id);

    testCases.forEach((test) => {
      if (filteredTests.includes(test.id)) {
        test.checked = checked;
      }
    });

    this.setState({ testCases, selectAll: checked });
  }

  onViewChangeClicked(viewType) {
    this.setState({ viewType });
  }

  // misc
  toggleModal(modal, element) {
    return () => {
      this.setState({
        showModal: !!modal,
        modalElement: element,
        modal,
      });
    };
  }

  closeModal() {
    this.setState({
      showModal: false,
      modal: '',
      executionname: '',
      notificationEmail: '',
    });
  }

  setTargetProject(event) {
    this.setState({ targetProject: event.target.value });
  }

  onBranchChange(value) {
    this.setState({ gitBranch: value });
  }

  /**
   * Navigate to the Add Project page
   */
  navigateToAddProject() {
    const { match } = this.props;
    const category = match?.params?.category || '';
    this.props.history.push(`/hosting/${category}/add-project`);
  }

  /**
   * Navigate to the Manage Test Data full page view.
   *
   * Pass selected test case UUIDs via router state (clean URL) and also include
   * a query-string fallback for reload/new-tab scenarios.
   */
  navigateToManageTestData() {
    const { match } = this.props;
    const category = match?.params?.category || '';
    const projectname = this.state.activeProject?.projectname;
    const selectedIds = (this.state.testCases || [])
      .filter((t) => t.checked)
      .map((t) => t.id)
      .filter(Boolean);

    if (!projectname) {
      this.props.addNewNotification('No active project selected', 'warning');
      return;
    }

    if (!selectedIds.length) {
      this.props.addNewNotification(
        'Select at least one test case to manage test data.',
        'warning'
      );
      return;
    }

    const idsParam = encodeURIComponent(selectedIds.join(','));
    this.props.history.push({
      pathname: `/hosting/${category}/manage-test-data/${projectname}`,
      search: `?ids=${idsParam}`,
      state: { selectedTestCaseIds: selectedIds },
    });
  }

  /**
   * Navigate to the Add Test Case full page (create mode).
   * Requires an active project. Call after closing AddTestCaseModal when user chooses "Create a test".
   */
  navigateToAddTestCase() {
    const { match } = this.props;
    const category = match?.params?.category || '';
    const projectname = this.state.activeProject?.projectname;
    if (!projectname) {
      this.props.addNewNotification('No active project selected', 'warning');
      return;
    }
    this.props.history.push({
      pathname: `/hosting/${encodeURIComponent(category)}/add-test-case/${encodeURIComponent(projectname)}`,
      state: {
        initialProject: this.state.activeProject,
        initialMetafields: this.state.testCaseMetafields,
      },
    });
  }

  /**
   * Navigate to the Edit Test Case full page.
   */
  navigateToEditTestCase(testCase) {
    const { match } = this.props;
    const category = match?.params?.category || '';
    const projectname = this.state.activeProject?.projectname;
    if (!projectname || !testCase?.testcasename) return;
    this.props.history.push({
      pathname: `/hosting/${encodeURIComponent(category)}/edit-test-case/${encodeURIComponent(projectname)}/${encodeURIComponent(testCase.testcasename)}`,
      state: {
        initialProject: this.state.activeProject,
        initialMetafields: this.state.testCaseMetafields,
        initialTestCase: testCase,
      },
    });
  }

  // Helper method to get git repo URL from branches based on team and AWS account
  getGitRepoFromBranches(teamname, awsaccountid) {
    const { branches } = this.props;

    if (!branches || branches.length === 0 || !teamname || !awsaccountid) {
      return '';
    }

    const matchingBranch = branches.find(
      (branch) =>
        branch.teamname === teamname && branch.awsaccountid === awsaccountid
    );

    if (matchingBranch && matchingBranch.gitrepo) {
      // Strip .git suffix from branch URLs
      return matchingBranch.gitrepo.trim().replace(/\.git$/, '');
    }

    return '';
  }

  uploadFilesToS3AndUpdatePayload(payload, files, fileType, endpoint) {
    const { props, state } = this;
    const fileUploadRequests = [];
    const testCaseIDMap = new Map();
    const filesToRemoveFromS3 = [];
    const envListArr = Object.values(state.activeProject.envlist).map(
      (e) => e.displayname
    );

    // prepare requests to upload files to S3
    Object.entries(files).forEach(([key, envFiles]) => {
      const { workstream } = props;
      const project = state.activeProject.projectname;
      const testcase = payload[key].testcasename;

      testCaseIDMap.set(testcase, key);

      envListArr.forEach((env) => {
        if (envFiles[env]) {
          // support multiple files upload
          // creating a request for each file
          if (Array.isArray(envFiles[env])) {
            envFiles[env].forEach((file) => {
              fileUploadRequests.push(
                endpoint(workstream, project, testcase, file, env, fileType)
              );
            });
          } else {
            // support single file upload
            fileUploadRequests.push(
              endpoint(
                workstream,
                project,
                testcase,
                envFiles[env],
                env,
                fileType
              )
            );
          }
        }
      });
    });

    Object.entries(payload).forEach(([testcaseid, testcase]) => {
      const currentTestCase = state.testCases.find(
        (tc) => tc.id === testcaseid
      );

      // skip cloned (undefined) testcases created by replicating testdata
      if (!currentTestCase) {
        return;
      }

      envListArr.forEach((env) => {
        if (
          currentTestCase.testdata[env] &&
          currentTestCase.testdata[env].testdatafile
        ) {
          if (testcase.testdata[env] && !testcase.testdata[env].testdatafile) {
            filesToRemoveFromS3.push(
              currentTestCase.testdata[env].testdatafile
            );
          }
        }
        if (
          currentTestCase.testdata[env] &&
          currentTestCase.testdata[env].testiterationfile
        ) {
          if (
            testcase.testdata[env] &&
            !testcase.testdata[env].testiterationfile
          ) {
            filesToRemoveFromS3.push(
              currentTestCase.testdata[env].testiterationfile
            );
          }
        }
      });
    });

    // Upload test data files to S3
    return Promise.all(fileUploadRequests).then((responses) => {
      // update payload with returned URIs
      responses.forEach((response) => {
        const fileLocation = response.data.file_location;
        const testCaseName = response.data.testcasename;
        const environment = response.data.environment;
        const key = testCaseIDMap.get(testCaseName);

        if (payload[key].testdata[environment]) {
          payload[key].testdata[environment][fileType] = fileLocation;
        }
      });

      return { payload, filesToRemoveFromS3 };
    });
  }

  async uploadFilesToS3AndUpdatePayloadForNewTestDataFormat(
    payload,
    files,
    endpoint
  ) {
    const { props, state } = this;
    const fileUploadRequests = [];
    const testCaseIDMap = new Map();
    const filesToRemoveFromS3 = [];
    // prepare requests to upload files to S3
    Object.entries(files).forEach(([testCaseId, envFiles]) => {
      const { workstream } = props;
      const project = state.activeProject.projectname;
      const testcase = payload[testCaseId].testcasename;

      testCaseIDMap.set(testcase, testCaseId);

      Object.entries(envFiles).forEach(([env, rows]) => {
        Object.entries(rows).forEach(([rowIndex, file]) => {
          // push to fileUploadRequests only if file is valid File object
          const fileType = Object.keys(file)[0];
          const rowName =
            fileType === 'testdatafile' ? rows[rowIndex]._name : env;
          if (rows[rowIndex][fileType] instanceof File) {
            // Check if this is the execution endpoint (no rowName parameter)
            // Execution endpoint: (workstream, project, testcase, payload, env, filetype)
            // Onboard endpoint: (workstream, project, testcase, rowname, payload, env, filetype)
            const isExecutionEndpoint =
              endpoint.name === 'postHostingCreateExecutionTestDataFile' ||
              endpoint
                .toString()
                .includes('postHostingCreateExecutionTestDataFile');

            if (isExecutionEndpoint) {
              // For execution endpoint: no rowName parameter
              fileUploadRequests.push(
                endpoint(
                  workstream,
                  project,
                  testcase,
                  rows[rowIndex][fileType], // payload (File)
                  env, // env
                  fileType // filetype
                ).then((response) => ({
                  response,
                  rowIndex,
                  fileType,
                }))
              );
            } else {
              // For onboard endpoint: includes rowName parameter
              fileUploadRequests.push(
                endpoint(
                  workstream,
                  project,
                  testcase,
                  rowName, // rowname
                  rows[rowIndex][fileType], // payload (File)
                  env, // env
                  fileType // filetype
                ).then((response) => ({
                  response,
                  rowIndex,
                  fileType,
                }))
              );
            }
          }
        });
      });
    });

    // Check for files to remove from S3
    for (const testCaseId in files) {
      const originalTestCase = state.testCases.find(
        (tc) => tc.id === testCaseId
      );
      for (const envKey in files[testCaseId]) {
        const oldEnvKey = envKey.split('_')[1].toUpperCase();
        for (const fileKey in files[testCaseId][envKey]) {
          const file = files[testCaseId][envKey][fileKey];
          if (
            file.testdatafile === '' &&
            originalTestCase[envKey][fileKey]?.testdatafile
          ) {
            filesToRemoveFromS3.push(
              originalTestCase[envKey][fileKey].testdatafile
            );
          }
          // check if testiterationfile is not present in the new file object
          // but present in the original test case object
          if (fileKey === 'testiterationfile' && !file.testiterationfile) {
            // consider removal for old testdata key based format
            //  { ENV1: {...}, ENV2:{...} } testiterationfiles
            if (
              originalTestCase.testdata?.[oldEnvKey] &&
              originalTestCase.testdata[oldEnvKey].testiterationfile
            ) {
              filesToRemoveFromS3.push(
                originalTestCase.testdata[oldEnvKey].testiterationfile
              );
              // clear iteration file reference from the old test data format
              // this should occurs only once in an old testdata format test case
              // extract rest of the test data from the old test case object, except the iteration file
              const rest = Object.entries(
                originalTestCase.testdata[oldEnvKey]
              ).reduce((acc, [key, value]) => {
                if (key !== 'testiterationfile') {
                  acc[key] = value;
                }
                return acc;
              }, {});
              payload[testCaseId].testdata = {
                ...originalTestCase.testdata,
                [oldEnvKey]: {
                  ...rest,
                },
              };
              //removal for new testdata format (array of objects)
            } else if (originalTestCase[envKey]) {
              const originalIterationFile = originalTestCase[envKey].find(
                (testDataRow) => testDataRow.testiterationfile
              );
              if (originalIterationFile) {
                filesToRemoveFromS3.push(
                  originalIterationFile.testiterationfile
                );
              }
            }
          }
        }
      }
    }

    // Upload test data files to S3
    const responses = await Promise.all(fileUploadRequests);
    // update payload with returned URIs
    responses.forEach(({ response, fileType, rowIndex }) => {
      const fileLocation = response.data.file_location;
      const testCaseName = response.data.testcasename;
      const testdataEnvKey = response.data.environment;
      const testCaseId = testCaseIDMap.get(testCaseName);
      if (payload[testCaseId][testdataEnvKey]) {
        if (fileType === 'testdatafile') {
          payload[testCaseId][testdataEnvKey][rowIndex][fileType] =
            fileLocation;
        } else {
          // instead of pushing the file location to the current row
          // we add it as a key to the first row of testdata object
          payload[testCaseId][testdataEnvKey][0] = {
            ...payload[testCaseId][testdataEnvKey][0],
            [fileType]: fileLocation,
          };
        }
      }
    });
    return { payload, filesToRemoveFromS3 };
  }

  submitTestCases(payload, files) {
    const { props, state } = this;
    console.log(this.state.gitBranch);
    const testDataEnvKey = `testdata_${props.activeEnvironment}`.toLowerCase();

    // Set loading state to true immediately
    this.setState({ isSubmittingTestCases: true });

    return this.uploadFilesToS3AndUpdatePayloadForNewTestDataFormat(
      payload,
      files,
      postHostingCreateExecutionTestDataFile
    )
      .then(({ payload }) => {
        const newPayload = [];

        Object.keys(payload).forEach((testCaseId) => {
          const testCase = payload[testCaseId];
          const testDataRows = testCase[testDataEnvKey];
          const testCaseDetails = {
            displayname: testCase.displayname,
            testcasename: testCase.testcasename,
            description: testCase.description,
            os: testCase.os,
            testcasedir: testCase.testcasedir,
            parameters: testCase.parameters,
            notsupportedenv: testCase.notsupportedenv,
            wiki: testCase.wiki,
            lineitems: testCase.lineitems,
          };

          if (
            state.activeProject.tools === BUILD_TOOLS.PLAYWRIGHT &&
            testCase.runAsSingleTransaction
          ) {
            newPayload.push({
              ...testCaseDetails,
              testdata: testDataRows,
            });
          } else {
            // if testDataRows length is 0, add the test case with empty test data
            // this is to handle the case where environment is added dinamically
            // and there is no test data for the new environment

            if (testDataRows.length === 0) {
              newPayload.push({
                ...testCaseDetails,
                testdata: [{}],
              });
            }
            testDataRows.forEach((row, index) => {
              const iterationFile =
                files[testCaseId][testDataEnvKey].testiterationfile;
              newPayload.push({
                ...testCaseDetails,
                displayname: `${testCase.displayname}_${row._name || index}`,
                // include test iteration file in test data object if it exists
                testdata: iterationFile
                  ? [
                      {
                        ...row,
                        testiterationfile: iterationFile.testiterationfile,
                      },
                    ]
                  : [row],
                testcasedir: testCase.testcasedir,
                parameters: testCase.parameters,
                notsupportedenv: testCase.notsupportedenv,
                wiki: testCase.wiki,
                lineitems: testCase.lineitems,
              });
            });
          }
        });

        this.props.submitTestCases(this.state.activeProject.projectname, {
          env: this.props.activeEnvironment,
          executionname: this.state.executionname,
          notificationemail: (
            this.state.notificationEmail || this.props.userInfo.emailid
          )
            .split(',')
            .map((email) => email.trim())
            .join(','),
          jiraTestExecutionId: this.state.jiraTestExecutionId,
          testcases: newPayload,
          workstreamname: this.props.workstream,
          gitbranch: this.state.gitBranch,
        });
        this.setState({
          ...this.state,
          executionname: '',
          notificationEmail: '',
          jiraTestExecutionId: undefined,
          isSubmittingTestCases: false,
        });

        this.props.setDefaultFilter(this.props.org);
      })
      .catch((error) => {
        // Reset loading state on error
        this.setState({ isSubmittingTestCases: false });
        // Re-throw the error so it can be handled by the caller if needed
        throw error;
      });
  }

  submitNewProject(payload) {
    return postHostingNewProject({
      org: this.props.org,
      workstreamname: this.props.workstream,
      ...payload.id,
    }).then((response) => {
      this.loadProjects();

      this.props.addNewNotification(response.data.message, 'success');
      this.closeModal();

      return response;
    });
  }

  submitUploadTestCases(payload) {
    const chunkSize = 50;
    const testcases = payload.slice(0, chunkSize);

    testcases.forEach((testcase) => {
      if (
        testcase.notsupportedenv &&
        typeof testcase.notsupportedenv === 'string'
      ) {
        testcase.notsupportedenv = testcase.notsupportedenv
          .replaceAll(' ', '')
          .split('|');
      } else {
        testcase.notsupportedenv = [];
      }
    });

    return postHostingNewTestCase({
      workstreamname: this.props.workstream,
      projectname: this.state.activeProject.projectname,
      testcases,
    }).then((response) => {
      this.loadTestCases(
        this.props.org,
        this.props.workstream,
        this.state.activeProject.projectname
      );

      this.props.addNewNotification(response.data.message, 'success');
      this.closeModal();

      return response;
    });
  }

  submitNewTestCase(payload) {
    const testcases = Object.keys(payload).map((key) => {
      return {
        ...payload[key],
        notsupportedenv: payload[key].notsupportedenv || [],
      };
    });

    return postHostingNewTestCase({
      workstreamname: this.props.workstream,
      projectname: this.state.targetProject,
      testcases,
    }).then((response) => {
      this.loadTestCases(
        this.props.org,
        this.props.workstream,
        this.state.activeProject.projectname
      );

      this.props.addNewNotification(response.data.message, 'success');
      this.closeModal();

      return response;
    });
  }

  submitEditTestCase(payload) {
    const selectedTestCase = this.state.modalElement;

    return putHostingEditTestCase(
      this.props.workstream,
      this.state.activeProject.projectname,
      selectedTestCase.testcasename,
      {
        projectname: this.state.activeProject.projectname,
        ...payload[selectedTestCase.id],
      }
    ).then((response) => {
      this.loadTestCases(
        this.props.org,
        this.props.workstream,
        this.state.activeProject.projectname
      );

      this.props.addNewNotification(response.data.message, 'success');
      this.closeModal();

      return response;
    });
  }

  async editTestCasesBulk(testCases) {
    const projectname = this.state.activeProject.projectname;
    const testCasesPayload = Object.keys(testCases).map((key) => {
      return {
        ...testCases[key],
        projectname,
      };
    });
    await this.submitBulkEditTestCases(testCasesPayload);
  }

  async moveTestCase(targetProject) {
    const projectname = this.state.activeProject.projectname;
    const selectedTestCase = this.state.modalElement;
    const testCasesPayload = [
      {
        ...selectedTestCase,
        projectname,
        toproject: targetProject,
      },
    ];
    await this.submitBulkEditTestCases(testCasesPayload);
  }

  async moveTestCasesBulk(targetProject) {
    const projectname = this.state.activeProject.projectname;
    const testCasesPayload = this.state.testCases
      .filter((t) => t.checked)
      .map((t) => {
        return {
          ...t,
          projectname,
          toproject: targetProject,
        };
      });
    await this.submitBulkEditTestCases(testCasesPayload);
  }

  async submitBulkEditTestCases(testCasesPayload) {
    const editTestCasesPromises = testCasesPayload.map((testCase) => {
      return putHostingEditTestCase(
        this.props.workstream,
        this.state.activeProject.projectname,
        testCase.testcasename,
        testCase
      );
    });
    this.setState({ isLoading: true });
    try {
      const responses = await Promise.all(editTestCasesPromises);
      this.loadTestCases(
        this.props.org,
        this.props.workstream,
        this.state.activeProject.projectname
      );
      this.props.addNewNotification(
        'Test case(s) updated successfully',
        'success'
      );
      this.closeModal();
      return responses;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        formatSubmissionErrorDetail(detail) ??
        (typeof detail === 'string' ? detail : null) ??
        error.response?.data?.message ??
        'Failed to update test case(s).';
      this.props.addNewNotification(message, 'danger');
    } finally {
      this.setState({ isLoading: false });
    }
  }

  submitDeleteTestCase(testcase) {
    return () => {
      this.setState({ deleteTestCaseInProgress: true });

      deleteHostingTestCase(
        this.props.workstream,
        this.state.activeProject.projectname,
        testcase
      ).then((response) => {
        const testCases = Array.from(this.state.testCases);
        const currentTestCaseIndex = this.state.testCases.findIndex(
          (t) => t.testcasename === testcase
        );
        const currentTestCase = this.state.testCases[currentTestCaseIndex];

        testCases.splice(currentTestCaseIndex, 1);

        if (currentTestCase.testdata.testdatafile) {
          deleteHostingTestDataFile(currentTestCase.testdata.testdatafile);
        }

        this.setState({ testCases, deleteTestCaseInProgress: false });
        this.props.addNewNotification(response.data.message, 'success');

        this.closeModal();
      });
    };
  }

  deleteTestCasesBulk() {
    const testCases = this.state.testCases.filter((t) => t.checked);
    // call deleteHostingTestCase api for each test case in an array of promisses
    const deleteTestCasesPromises = testCases.map(({ testcasename }) => {
      return deleteHostingTestCase(
        this.props.workstream,
        this.state.activeProject.projectname,
        testcasename
      );
    });
    this.setState({ deleteTestCaseInProgress: true });
    Promise.all(deleteTestCasesPromises)
      .then((responses) => {
        this.setState({
          testCases: this.state.testCases.filter((t) => !t.checked),
        });
        this.props.addNewNotification(
          'Test case(s) deleted successfully',
          'success'
        );
        // call deleteHostingTestDataFile api
        // for those test cases which have testdata.testdatafile
        const testCasesWithTestDataFile = testCases.filter((testCase) =>
          Boolean(testCase.testdata.testdatafile)
        );
        const deleteTestDataFilePromises = testCasesWithTestDataFile.map(
          (testCase) => {
            return deleteHostingTestDataFile(testCase.testdata.testdatafile);
          }
        );
        Promise.all(deleteTestDataFilePromises)
          .then((_) => {})
          .catch((error) => {
            const detail = error.response?.data?.detail;
            const message =
              formatSubmissionErrorDetail(detail) ??
              (typeof detail === 'string' ? detail : null) ??
              'Failed to delete test case(s).';
            this.props.addNewNotification(message, 'danger');
          });
        return responses;
      })
      .catch((error) => {
        const detail = error.response?.data?.detail;
        const message =
          formatSubmissionErrorDetail(detail) ??
          (typeof detail === 'string' ? detail : null) ??
          'Failed to delete test case(s).';
        this.props.addNewNotification(message, 'danger');
      })
      .finally(() => {
        this.setState({ deleteTestCaseInProgress: false });
        this.closeModal();
      });
  }

  submitAddTestData(payload, files) {
    const { props, state } = this;

    return this.uploadFilesToS3AndUpdatePayloadForNewTestDataFormat(
      payload,
      files,
      postHostingCreateTestDataFile
    ).then(({ payload, filesToRemoveFromS3 }) => {
      // submit the payload to update/create test data
      return postHostingCreateTestData({
        projectname: state.activeProject.projectname,
        workstreamname: props.workstream,
        testcases: Object.values(payload),
      }).then((response) => {
        this.loadTestCases(
          props.org,
          props.workstream,
          state.activeProject.projectname
        );

        const deleteFilesPromisses = filesToRemoveFromS3.map((file) =>
          deleteHostingTestDataFile(file)
        );

        Promise.all(deleteFilesPromisses)
          .then((responses) => {
            responses.forEach((response) => {
              if (response.data.status === 'success') {
                this.props.addNewNotification(response.data.message, 'success');
              } else {
                const detail = response.data.detail;
                const message =
                  formatSubmissionErrorDetail(detail) ??
                  (typeof detail === 'string' ? detail : null) ??
                  'Operation failed.';
                this.props.addNewNotification(message, 'danger');
              }
            });
          })
          .catch((error) => {
            const detail = error.response?.data?.detail;
            const message =
              formatSubmissionErrorDetail(detail) ??
              (typeof detail === 'string' ? detail : null) ??
              'Operation failed.';
            this.props.addNewNotification(message, 'danger');
          });
        this.closeModal();

        return response;
      });
    });
  }

  submitGitHubTestCases(payload) {
    this.setState({ isLoading: true });

    return postGitHubFetchTestCases(payload)
      .then((response) => {
        console.log(
          '🎉 GitHub test cases fetched successfully:',
          response.data
        );

        if (
          !response.data ||
          !Array.isArray(response.data) ||
          response.data.length === 0
        ) {
          this.props.addNewNotification(
            'No test cases found in the specified path or invalid response.',
            'warning'
          );
          this.closeModal();
          return;
        }

        // Build a quick lookup set of existing test case keys: "<file>::<name>"
        // Exclude test cases already marked as deleted from Git
        const normalizePath = (p) => (p || '').replace(/\\/g, '/');
        const existingKeys = new Set(
          (this.state.testCases || [])
            .filter((et) => !et.isGitSourceDeleted) // Exclude already-deleted test cases
            .map((et) => {
              const filePath = normalizePath(
                (et && (et.file_path || et.spec || et.testcasedir || '')) || ''
              );
              const name = (
                (et &&
                  (et.displayname ||
                    et.description ||
                    et.testcasename ||
                    '')) ||
                ''
              ).trim();
              const grep = ((et && (et.grep || '')) || '').trim();
              return `${filePath}::${name}::${grep}`.toLowerCase();
            })
        );

        const decorateFetched = (tc) => {
          // Compute normalized composite key for fetched test case
          const filePath = normalizePath(
            (tc && (tc.file_path || tc.file_name || tc.spec || '')) || ''
          );
          const name = (
            (tc && (tc.test_name || tc.description || '')) ||
            ''
          ).trim();
          const grep = ((tc && (tc.grep_name || tc.grep || '')) || '').trim();

          const key = `${filePath}::${name}::${grep}`.toLowerCase();
          const alreadyImported = !!name && existingKeys.has(key);
          if (!name) {
            console.warn(
              'Git import item without resolvable test_name/description. Skipping duplicate check for:',
              tc
            );
          }
          return {
            ...tc,
            _alreadyImported: alreadyImported,
            _normalizedKey: key,
          };
        };

        const decorated = response.data.map(decorateFetched);

        // Detect deleted test cases from Git source
        // Build Set of file paths from fetched items
        const fetchedFilePaths = new Set(
          decorated.map((tc) =>
            normalizePath(tc.file_path || tc.file_name || tc.spec || '')
          )
        );

        // Build Set of keys from fetched items
        const fetchedKeys = new Set(decorated.map((tc) => tc._normalizedKey));

        // Find existing test cases that:
        // 1. Were imported from Git (isGitImported = true)
        // 2. NOT already marked as deleted (isGitSourceDeleted !== true)
        // 3. Have file_path matching one of the fetched files
        // 4. Are NOT in the current fetch response
        const deletedTestCases = (this.state.testCases || []).filter(
          (existingTC) => {
            if (!existingTC.isGitImported) return false;

            // Skip test cases already marked as deleted
            if (existingTC.isGitSourceDeleted) return false;

            const existingFilePath = normalizePath(
              existingTC.file_path ||
                existingTC.spec ||
                existingTC.testcasedir ||
                ''
            );

            // Only check test cases from files that were fetched
            if (!fetchedFilePaths.has(existingFilePath)) return false;

            // Build key for existing test case
            const existingName = (
              existingTC.displayname ||
              existingTC.description ||
              existingTC.testcasename ||
              ''
            ).trim();
            const existingGrep = (existingTC.grep || '').trim();
            const existingKey =
              `${existingFilePath}::${existingName}::${existingGrep}`.toLowerCase();

            // If key not in fetched set, it was deleted
            return !fetchedKeys.has(existingKey);
          }
        );

        if (deletedTestCases.length > 0) {
          console.log(
            `🗑️ Detected ${deletedTestCases.length} test case(s) deleted from Git source`
          );
        }

        this.setState(
          {
            fetchedTestCases: decorated,
            deletedGitTestCases: deletedTestCases,
          },
          () => {
            this.toggleModal('selectGitHubTestCases')();
          }
        );
      })
      .catch((error) => {
        console.error('❌ Error fetching GitHub test cases:', error);
        const detail = error.response?.data?.detail;
        const errorMessage =
          formatSubmissionErrorDetail(detail) ??
          (typeof detail === 'string' ? detail : null) ??
          error.response?.data?.message ??
          'Failed to fetch test cases from GitHub';
        this.props.addNewNotification(errorMessage, 'danger');
        this.closeModal();
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  createGitHubTestCases(selectedTestCases) {
    console.log('📊 Creating GitHub test cases:', selectedTestCases);

    // Store selected test cases in state for later use
    this.setState({ selectedTestCasesForImport: selectedTestCases });

    // Check if there are deleted test cases
    if (this.state.deletedGitTestCases.length > 0) {
      console.log('⚠️ Detected deleted test cases, showing confirmation modal');
      this.closeModal(); // Close the select modal
      this.toggleModal('deletedGitTestCases')();
    } else {
      // No deleted test cases, proceed with normal creation
      this.processTestCaseCreation(selectedTestCases);
    }
  }

  processTestCaseCreation(selectedTestCases) {
    const testcasesToCreate = selectedTestCases
      .map((tc) => {
        if (tc.description) {
          // Format 1
          const testCase = {
            displayname: tc.description,
            testcasename: `${tc.group_name}.${tc.method_name}`,
            description: tc.description,
            testcasedir: tc.file_name,
            file_path: tc.file_path,
            entity_type: tc.entity_type || 'test',
            isGitImported: true,
            notsupportedenv: [],
          };

          // Add jiraId to parameters if jira_id exists and has a value
          if (tc.jira_id) {
            testCase.parameters = { jiraId: tc.jira_id };
          }

          return testCase;
        } else if (tc.test_name) {
          // Format 2
          const testCase = {
            displayname: tc.test_name,
            testcasename: tc.test_name,
            description: tc.test_name,
            spec: tc.file_name,
            grep: tc.grep_name,
            file_path: tc.file_path,
            entity_type: tc.entity_type || 'test',
            isGitImported: true,
            env: 'STG',
            testdata: {},
            parameters: {},
            notsupportedenv: [],
          };

          // Add jiraId to parameters if jira_id exists and has a value
          if (tc.jira_id) {
            testCase.parameters.jiraId = tc.jira_id;
          }

          return testCase;
        }
        return null;
      })
      .filter(Boolean);

    if (testcasesToCreate.length === 0) {
      this.props.addNewNotification('No test cases were selected.', 'warning');
      this.closeModal();
      return;
    }

    this.setState({ isLoading: true });

    return postHostingNewTestCase({
      workstreamname: this.props.workstream,
      projectname: this.state.activeProject.projectname,
      testcases: testcasesToCreate,
    })
      .then((response) => {
        this.loadTestCases(
          this.props.org,
          this.props.workstream,
          this.state.activeProject.projectname
        );
        this.props.addNewNotification(
          response.data.message || 'Test cases created successfully!',
          'success'
        );
        this.closeModal();
      })
      .catch((error) => {
        const detail = error.response?.data?.detail;
        const errorMessage =
          formatSubmissionErrorDetail(detail) ??
          (typeof detail === 'string' ? detail : null) ??
          error.response?.data?.message ??
          'Failed to create test cases.';
        this.props.addNewNotification(errorMessage, 'danger');
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  handleDeletedTestCasesContinue() {
    const deletedTestCases = this.state.deletedGitTestCases;
    const selectedTestCases = this.state.selectedTestCasesForImport;

    // Close modal immediately for better UX
    this.closeModal();

    if (deletedTestCases.length === 0) {
      // No deleted test cases, just proceed with import
      this.processTestCaseCreation(selectedTestCases);
      return;
    }

    // Show notification about the process starting
    this.props.addNewNotification(
      `Importing ${selectedTestCases.length} test case(s) and marking ${deletedTestCases.length} as deleted...`,
      'info'
    );

    // Mark deleted test cases as invalid in parallel with import
    const deletionPromises = deletedTestCases.map((tc) =>
      putHostingEditTestCase(
        this.props.workstream,
        this.state.activeProject.projectname,
        tc.testcasename,
        {
          ...tc,
          projectname: this.state.activeProject.projectname,
          isGitSourceDeleted: true,
        }
      )
    );

    // Run both operations in parallel
    const deletionOperation = Promise.all(deletionPromises)
      .then(() => {
        this.props.addNewNotification(
          `${deletedTestCases.length} test case(s) marked as deleted from Git.`,
          'success'
        );
      })
      .catch((error) => {
        console.error('Error marking deleted test cases as invalid:', error);
        this.props.addNewNotification(
          'Failed to mark some test cases as deleted.',
          'warning'
        );
      });

    // Start import (which will reload test cases when done)
    const importOperation = this.processTestCaseCreation(selectedTestCases);

    // Wait for both operations, then do a final reload to ensure everything is up-to-date
    Promise.all([deletionOperation, importOperation])
      .then(() => {
        // Final reload to ensure both deletions and new imports are visible
        this.loadTestCases(
          this.props.org,
          this.props.workstream,
          this.state.activeProject.projectname
        );
      })
      .catch(() => {
        // Error already handled by individual operations
      });
  }

  // render methods
  renderProjects(projects) {
    // Show loading spinner while data is being fetched
    if (!this.state.projectMetafields.length) {
      return <LoadingSpinner text="Loading projects" />;
    }

    // Show empty state when no projects exist
    if (projects.length === 0) {
      return <EmptyProjectsState onCreateProject={this.navigateToAddProject} />;
    }

    // Render projects list
    const channels = projects.map((p) => ({
      subcategoryname: p.displayname,
      subcategoryid: p.projectname,
      status: p.status,
    }));
    const channelsProps = {
      channels,
      onClick: this.onProjectSelected,
      selectedChannel: this.state.activeProject.projectname,
      actions: [],
    };

    if (this.props.role === 'admin') {
      channelsProps.actions.push(
        {
          name: 'create-project',
          text: 'Add project',
          onClick: this.navigateToAddProject,
        },
        {
          name: 'global-fields',
          text: 'Global fields',
          onClick: () => {
            const category = this.props.match?.params?.category || '';
            this.props.history.push(`/hosting/${category}/global-fields`);
          },
        },
        {
          name: 'add-branch',
          text: 'Manage Git Branch',
          onClick: this.toggleModal('gitBranches'),
        }
      );

      channelsProps.activeChannelSecondaryAction = {
        text: <i className="project-icon bi bi-pencil-fill"></i>,
        onClick: () => {
          const { match } = this.props;
          const category = match?.params?.category || '';
          this.props.history.push(
            `/hosting/${category}/edit-project/${this.state.activeProject.projectname}`
          );
        },
      };
    }

    return <Channels {...channelsProps} />;
  }

  renderTestCases(testCases) {
    const { state } = this;
    const projectsLoaded = state.projects.length > 0;
    const hasActiveProject =
      state.activeProject && state.activeProject.projectname;
    const testCasesFetched =
      !state.loadingTestCases && state.projectMetafields.length > 0;

    if (state.loadingTestCases && !testCases.length) {
      return <LoadingSpinner text="Loading test cases" />;
    }

    // Show empty state only when:
    // 1. Projects have been loaded
    // 2. An active project is selected
    // 3. Test cases have been fetched (API call completed)
    // 4. There are no test cases under the project
    if (
      projectsLoaded &&
      hasActiveProject &&
      testCasesFetched &&
      testCases.length === 0
    ) {
      return (
        <EmptyTestCasesState
          onAddTest={this.toggleModal('newTestCase')}
          isAdmin={this.props.role === 'admin'}
        />
      );
    }

    // if table view is selected
    const filteredTests = filterList(
      testCases,
      state.searchValue,
      'displayname'
    ).filter((test) => {
      return test.status[this.props.activeEnvironment] !== 'disabled';
    });

    if (state.viewType === 'table') {
      const selectedTestCases = filteredTests
        .filter((t) => t.checked)
        .map((t) => t.id);

      const handleSelectionChange = (newSelection) => {
        const selectionSet = new Set(newSelection);
        const updatedTestCases = this.state.testCases.map((t) => {
          const isVisible = filteredTests.find((ft) => ft.id === t.id);
          if (isVisible) {
            return { ...t, checked: selectionSet.has(t.id) };
          }
          return t;
        });

        let { selectAll } = this.state;
        if (selectAll) {
          const anyUnchecked = filteredTests.some(
            (t) => !selectionSet.has(t.id)
          );
          if (anyUnchecked) {
            selectAll = false;
          }
        }

        this.setState({ testCases: updatedTestCases, selectAll });
      };

      const handleEdit = (testCase) => {
        this.navigateToEditTestCase(testCase);
      };

      const handleDelete = (testCase) => {
        this.toggleModal('deleteTestCaseConfirmation', testCase.testcasename)();
      };

      const handleClone = (testCase) => {
        this.toggleModal('cloneTestCase', testCase)();
      };

      const handleMove = (testCase) => {
        this.toggleModal('moveTestCase', testCase)();
      };

      const handleView = (testCase) => {
        this.toggleModal('showTestCase', testCase)();
      };

      const handleRun = (testCase) => {
        this.toggleModal('submitTestCases', testCase)();
      };

      return (
        <HostingTable
          testCases={filteredTests}
          selectedTestCases={selectedTestCases}
          onSelectionChange={handleSelectionChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onClone={handleClone}
          onMove={handleMove}
          onView={handleView}
          onRun={handleRun}
          role={this.props.role}
        />
      );
    }

    // Grid view
    const tests = filterList(testCases, state.searchValue, 'displayname').map(
      (testCase) => {
        if (testCase.status[this.props.activeEnvironment] === 'disabled') {
          return null;
        }

        const testCaseProps = {
          checked: testCase.checked,
          description: testCase.description,
          name: testCase.displayname,
          testcaseid: testCase.id,
          onItemSelected: this.onTestCaseSelected,
          onLabelClick: this.toggleModal('showTestCase', testCase),
          learnMoreUrl: testCase.wiki,
          isGitSourceDeleted: testCase.isGitSourceDeleted,
        };

        // Add Run action for all users (not just admin)
        testCaseProps.secondaryActions = [
          {
            name: 'run-test-case',
            text: 'Run',
            onClick: this.toggleModal('submitTestCases', testCase),
          },
        ];

        if (this.props.role === 'admin') {
          testCaseProps.secondaryActions.push(
            {
              name: 'edit-test-case',
              text: 'Edit',
              onClick: () => this.navigateToEditTestCase(testCase),
            },
            {
              name: 'delete-test-case',
              text: 'Delete',
              onClick: this.toggleModal(
                'deleteTestCaseConfirmation',
                testCase.testcasename
              ),
            },
            {
              name: 'clone-test-case',
              text: 'Clone',
              onClick: this.toggleModal('cloneTestCase', testCase),
            },
            {
              name: 'move-test-case',
              text: 'Move',
              onClick: this.toggleModal('moveTestCase', testCase),
            }
          );
        }

        return (
          <div
            className="scenario"
            key={testCase.testcasename.split(' ').join('-')}
          >
            <TestCaseCard {...testCaseProps} key={testCase.id} />
          </div>
        );
      }
    );

    const emptyItems = [
      ...Array(
        Math.ceil(tests.filter((t) => t).length / 3) * 3 -
          tests.filter((t) => t).length
      ).keys(),
    ];
    const placeholders = emptyItems.map((idx) => (
      <div className="scenario empty" key={`empty-${idx}`}></div>
    ));

    return [...tests, ...placeholders];
  }

  renderModal() {
    const { props, state } = this;
    let selectedTestCases = JSON.stringify(
      state.testCases.filter((t) => t.checked)
    );

    selectedTestCases = JSON.parse(selectedTestCases);

    if (state.showModal) {
      let modalProps = {
        environment: props.activeEnvironment,
        onClose: this.closeModal,
      };

      switch (state.modal) {
        case 'createProject':
          modalProps = {
            ...modalProps,
            elements: [{ id: 'id' }],
            metafields: this.state.projectMetafields,
            onSubmit: this.submitNewProject,
            title: 'Create new project',
            branches: this.props.branches, // Pass branches for auto-populating gitrepourl
          };

          break;
        case 'showTestCase':
          modalProps = {
            ...modalProps,
            elements: [this.state.modalElement],
            metafields: this.state.testCaseMetafields,
            valueFromElements: true,
            modal: state.modal,
            title: this.state.modalElement.displayname,
            parent: this.state.activeProject,
            readOnlyFields: this.state.testCaseMetafields.map((f) => f.field),
            hiddenFields: ['notsupportedenv'],
          };

          break;
        case 'newTestCase':
          return (
            <AddTestCaseModal
              open={state.showModal}
              onClose={this.closeModal}
              onUpload={this.toggleModal('uploadTestCases')}
              onCreateTest={() => {
                this.closeModal();
                this.navigateToAddTestCase();
              }}
              onCreateFromGit={this.toggleModal('createTestCaseFromGit')}
            />
          );
        case 'uploadTestCases':
          modalProps = {
            ...modalProps,
            onSubmit: this.submitUploadTestCases,
            fields: this.state.testCaseMetafields
              .map((f) => f.field)
              .filter((f) => !['env', 'testdata'].includes(f)),
            instructions:
              'To upload multiple tests cases at a time please use the provided CSV template. At the moment Testing Hub only supports CSV files with up to 50 test cases. Additional test cases will be ignored.',
          };

          return <BulkUploadModal {...modalProps} />;
        case 'createTestCase':
          modalProps = {
            ...modalProps,
            elements: [{ id: 'id' }],
            metafields: this.state.testCaseMetafields,
            onSubmit: this.submitNewTestCase,
            hiddenFields: ['env', 'testdata'],
            title: 'Create new test case',
            parent: this.state.activeProject,
          };

          if (this.state.activeProject.tools === 'maven') {
            modalProps.hiddenFields.push('testcasename');
          }

          break;
        case 'createTestCaseFromGit':
          return (
            <GitTestCaseModal
              onClose={this.closeModal}
              onSubmit={this.submitGitHubTestCases}
              loading={this.state.isLoading}
              parent={this.state.activeProject}
            />
          );
        case 'cloneTestCase':
          modalProps = {
            ...modalProps,
            elements: [{ ...this.state.modalElement, id: 'id' }],
            metafields: this.state.testCaseMetafields,
            onSubmit: this.submitNewTestCase,
            valueFromElements: true,
            hiddenFields: ['env', 'testdata', 'testcasename'],
            readOnlyFields: [],
            title: 'Clone test case',
            parent: this.state.activeProject,
          };

          break;
        case 'cloneTestCasesBulk':
          modalProps = {
            ...modalProps,
            elements: state.testCases.filter((t) => t.checked),
            metafields: [
              {
                name: 'Target project',
                field: 'toproject',
                type: 'select',
                default: this.state.activeProject.projectname,
                options: this.state.projects.map((project) => ({
                  id: project.projectname,
                  name: project.displayname,
                })),
              },
              ...this.state.testCaseMetafields,
            ],
            onSubmit: this.submitNewTestCase,
            valueFromElements: true,
            hiddenFields: ['env', 'testdata', 'testcasename', 'toproject'],
            readOnlyFields: [],
            title: 'Clone Test Cases in Bulk',
            parent: this.state.activeProject,
            onChangeProject: this.setTargetProject,
          };

          break;
        case 'moveTestCase':
          return (
            <MoveTestCasesModal
              projects={this.state.projects.map((project) => ({
                id: project.projectname,
                displayname: project.displayname,
              }))}
              onClose={this.closeModal}
              onSubmit={this.moveTestCase}
              sourceProject={this.state.activeProject.projectname}
              loading={this.state.isLoading}
              selectedTestCases={[
                state.modalElement?.displayname || state.modalElement?.testcasename
              ].filter(Boolean)}
            />
          );

        case 'moveTestCasesBulk':
          return (
            <MoveTestCasesModal
              projects={this.state.projects.map((project) => ({
                id: project.projectname,
                displayname: project.displayname,
              }))}
              onClose={this.closeModal}
              onSubmit={this.moveTestCasesBulk}
              sourceProject={this.state.activeProject.projectname}
              loading={this.state.isLoading}
              selectedTestCases={state.testCases
                .filter((t) => t.checked)
                .map((t) => t.displayname)}
            />
          );

        case 'editTestCase':
          modalProps = {
            ...modalProps,
            elements: [this.state.modalElement],
            metafields: this.state.testCaseMetafields,
            onSubmit: this.submitEditTestCase,
            valueFromElements: true,
            hiddenFields: ['env', 'testdata'],
            readOnlyFields: ['testcasename'],
            title: 'Edit test case',
            parent: this.state.activeProject,
          };

          break;

        case 'editTestCasesBulk':
          modalProps = {
            ...modalProps,
            elements: state.testCases.filter((t) => t.checked),
            metafields: this.state.testCaseMetafields,
            onSubmit: this.editTestCasesBulk,
            valueFromElements: true,
            hiddenFields: ['env', 'testdata'],
            readOnlyFields: ['testcasename'],
            title: 'Edit Test Cases in Bulk',
            parent: this.state.activeProject,
          };

          break;
        case 'submitTestCases':
          const metafields = this.state.testCaseMetafields;
          console.log(metafields);

          metafields.find((m) => m.field === 'env').default =
            this.props.activeEnvironment;

          // Use modalElement (single test case from Run button) if available, otherwise use selected test cases
          const testCasesToSubmit = state.modalElement
            ? [state.modalElement]
            : selectedTestCases;

          modalProps = {
            ...modalProps,
            elements: transformTestData(testCasesToSubmit),
            metafields: metafields,
            onSubmit: this.submitTestCases,
            onBranchChange: this.onBranchChange,
            valueFromElements: true,
            readOnlyFields: ['testcasedir'],
            hiddenFields: [
              'wiki',
              'notsupportedenv',
              'parameters',
              'env',
              'testcasename',
              'displayname',
              'description',
              'tools',
              'os',
              'testClass',
              'testGroup',
              'testMethod',
              'spec',
              'grep',
              'grepInvert',
              'project',
            ],
            title: 'Submit test case',
            parent: this.state.activeProject,
            isSubmittingFiles: this.state.isSubmittingTestCases,
            commonFields: [
              <div className="col-sm-12 form-group">
                <div className="row">
                  <div className="col-4 form-group" key="executionname">
                    <label htmlFor="executionname">
                      Execution name <span className="optional">Optional</span>
                    </label>
                    <input
                      className="form-control"
                      name="executionname"
                      type="text"
                      onChange={this.onCommonFieldChange}
                      placeholder="Enter a name or short note for this execution"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-4 form-group" key="notificationemail">
                    <NotificationField
                      value={state.notificationEmail || props.userInfo.emailid}
                      notifyAllValue={state.activeProject.emaildl}
                      onChange={this.onNotificationEmailChange}
                      loggedInUserEmail={props.userInfo.emailid}
                    />
                  </div>

                  <div className="col-4 form-group">
                    <label htmlFor="jirafield">
                      Update JIRA <span className="optional">Optional</span>
                    </label>
                    <div className="form-group">
                      <JiraFields onChange={this.onCommonFieldChange} />
                    </div>
                  </div>
                </div>
              </div>,
            ],
          };
          return <SubmitTestCaseModal {...modalProps} />;

        case 'deleteTestCaseConfirmation':
          return (
            <ConfirmationModal
              message={
                'Do you want to delete the selected test case? This action cannot be undone.'
              }
              onClose={this.closeModal}
              onConfirm={this.submitDeleteTestCase(this.state.modalElement)}
              loading={this.state.deleteTestCaseInProgress}
            />
          );
        case 'deleteTestCasesBulk':
          return (
            <ConfirmationModal
              message={`Do you want to delete ${
                state.testCases.filter((t) => t.checked).length
              } selected test cases? This action cannot be undone.`}
              onClose={this.closeModal}
              onConfirm={this.deleteTestCasesBulk}
              loading={this.state.deleteTestCaseInProgress}
            >
              <div className="modal-form-common-fields">
                <hr />
                {state.testCases
                  .filter((t) => t.checked)
                  .map((t) => (
                    <div className="testcase-list-item">{t.displayname}</div>
                  ))}
              </div>
            </ConfirmationModal>
          );
        case 'gitBranches':
          return (
            <BranchModal
              onClose={this.closeModal}
              org={this.props.org}
              workstream={this.props.workstream}
              teams={
                this.state.projectMetafields.find((f) => f.field === 'teamname')
                  ?.options || []
              }
              awsaccounts={
                this.state.projectMetafields.find(
                  (f) => f.field === 'awsaccountid'
                )?.options || []
              }
            />
          );
        case 'selectGitHubTestCases':
          return (
            <SelectGitHubTestCasesModal
              onClose={this.closeModal}
              onSubmit={this.createGitHubTestCases}
              testCases={this.state.fetchedTestCases}
              loading={this.state.isLoading}
            />
          );
        case 'deletedGitTestCases':
          return (
            <DeletedGitTestCasesModal
              onClose={this.closeModal}
              onContinue={this.handleDeletedTestCasesContinue}
              deletedTestCases={this.state.deletedGitTestCases}
              loading={this.state.isLoading}
            />
          );
        default:
          break;
      }

      return <MetafieldsModal {...modalProps} />;
    }

    return null;
  }

  render() {
    const {
      projectMetafields,
      loadingTestCases,
      testCases: stateTestCases,
      projects: stateProjects,
      activeProject,
    } = this.state;
    const projects = this.renderProjects(stateProjects);
    const testCases = this.renderTestCases(stateTestCases);
    const modal = this.renderModal();
    const filteredTests = filterList(
      stateTestCases,
      this.state.searchValue,
      'displayname'
    ).filter((test) => {
      return test.status[this.props.activeEnvironment] !== 'disabled';
    });

    const selectedTestCases = stateTestCases.filter((s) => s.checked);
    const hasTestCases = stateTestCases.length > 0;
    const testCasesFetched = !loadingTestCases && projectMetafields.length > 0;

    return (
      <div className="hosting-wrapper">
        <section className="section-title">
          <div className="section-title-group">
            <h2 className="title">
              {this.props.workstreamName || 'Loading...'}
            </h2>
          </div>
        </section>

        <div className="hosting-projects" style={{ marginBottom: '16px' }}>
          {projects}
        </div>

        {/* Show full controls when there are test cases */}
        {testCasesFetched && hasTestCases && (
          <HostingControls
            available={filteredTests.length}
            selected={selectedTestCases.length}
            total={stateTestCases.length}
            onSelectAll={this.onSelectAllClicked}
            selectedAll={this.state.selectAll}
            showModalWithContent={this.toggleModal}
            onManageTestData={this.navigateToManageTestData}
            onChangeView={this.onViewChangeClicked}
            viewType={this.state.viewType}
            onSearchInputChange={this.onSearchInputChange}
            searchValue={this.state.searchValue}
            environments={activeProject.envlist}
            context={this.props.workstream}
            projects={this.state.projects}
          />
        )}

        <div className={`scenarios ${this.state.viewType}`}>{testCases}</div>

        {modal}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { menu, environment, userInfo } = state.app;
  const { service } = menu;
  const { globalFields, branches } = state.hosting;

  return {
    workstream: menu[service],
    workstreamName: menu.categoryName,
    org: service,
    activeEnvironment: environment[menu[service]],
    role: state.hosting.role,
    userInfo: userInfo,
    globalFields,
    branches: branches || [],
  };
};

const mapDispatchToProps = (dispatch) => ({
  addNewNotification: (message, category) =>
    dispatch(addNewNotification(message, category)),
  submitTestCases: (project, payload) =>
    dispatch(submitTestCases(project, payload)),
  setDefaultFilter: (source) => dispatch(setDefaultFilter(source)),
  removeHostingProject: (workstream, project) =>
    dispatch(removeHostingProject(workstream, project)),
  loadGitBranches: (branches) => dispatch(loadGitBranches(branches)),
});

const ConnectedHosting = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Hosting)
);

const HostingWithTheme = (props) => (
  <WeaveMuiThemeProvider>
    <ConnectedHosting {...props} />
  </WeaveMuiThemeProvider>
);

export default HostingWithTheme;
